import * as React from 'react';

import * as api from '@gitbook/api-client';
import { assertNever } from '@gitbook/util-ts';

import { FilesImportModal } from './FilesImportModal';
import { useOrganizationImportTargetSpace } from './hooks';
import {
    type GetSpace,
    type ImportModalProps,
    type ImportTarget,
    isOrganizationImportTarget,
    type OrganizationImportTarget,
    type SpaceImportTarget,
} from './types';
import type { ImportSource } from './useImportSources';
import { WebsiteImportModal } from './WebsiteImportModal';

interface ImportBySourceModalProps {
    sourceInfo?: ImportSource;
    startImport: ImportModalProps['startImport'];
    importing: boolean;
    target: ImportTarget;
    setSource: (source: api.ImportContentSource | null) => void;
    onBack: () => void;
    /* Used only for modern */
    onComplete: (space: api.Space) => void;
}

/**
 * Returns an import modal according to the given import source and target.
 * Uses either given space or creates a new one.
 */
export function ImportBySourceModal(props: ImportBySourceModalProps): React.ReactElement | null {
    return isOrganizationImportTarget(props.target) ? (
        <OrganizationImportTargetModal {...props} target={props.target} />
    ) : (
        <SpaceImportTargetModal {...props} target={props.target} />
    );
}

/**
 * Modal to use when importing into an existing space.
 */
function SpaceImportTargetModal(
    props: Omit<ImportBySourceModalProps, 'target'> & { target: SpaceImportTarget }
): React.ReactElement {
    const getSpace = React.useCallback(async () => {
        return props.target.space;
    }, [props.target.space]);

    return <ImportBySourceModalBody {...props} getSpace={getSpace} />;
}

/**
 * Modal to use when importing into a new space, in an organization.
 */
function OrganizationImportTargetModal(
    props: Omit<ImportBySourceModalProps, 'target'> & { target: OrganizationImportTarget }
): React.ReactElement | null {
    const getSpace = useOrganizationImportTargetSpace(props.target);

    // User doesn't have permission to create a space in the organization.
    if (!getSpace) {
        return null;
    }

    return <ImportBySourceModalBody {...props} getSpace={getSpace} />;
}

/**
 * Common body used in both modals.
 */
function ImportBySourceModalBody(
    props: ImportBySourceModalProps & {
        getSpace: GetSpace;
    }
): React.ReactElement | null {
    const { sourceInfo, startImport, importing, target, getSpace, onBack } = props;

    const { source } = sourceInfo ?? {};
    if (sourceInfo === undefined || source === undefined) {
        return null;
    }

    switch (source) {
        case api.ImportContentSource.Website:
        case api.ImportContentSource.GithubWiki: {
            return (
                <WebsiteImportModal
                    target={target}
                    title={sourceInfo.title}
                    subtitle={sourceInfo.subtitle}
                    getSpace={getSpace}
                    startImport={startImport}
                    importing={importing}
                    onClose={onBack}
                />
            );
        }

        case api.ImportContentSource.Docx:
        case api.ImportContentSource.Markdown:
        case api.ImportContentSource.Html:
        case api.ImportContentSource.Zip:
        case api.ImportContentSource.GoogleDocs:
        case api.ImportContentSource.Confluence:
        case api.ImportContentSource.Notion: {
            const limitWarning =
                source === api.ImportContentSource.Zip ||
                source === api.ImportContentSource.Confluence ||
                source === api.ImportContentSource.Notion;

            return (
                <FilesImportModal
                    target={target}
                    getSpace={getSpace}
                    title={sourceInfo.title}
                    subtitle={sourceInfo.subtitle}
                    steps={sourceInfo.steps}
                    fileIcon={sourceInfo.fileIcon}
                    fileExtension={sourceInfo.fileExtension!}
                    onClose={onBack}
                    startImport={startImport}
                    importing={importing}
                    limitWarning={limitWarning}
                />
            );
        }

        default:
            assertNever(source);
    }
}
